import React, { useState, useEffect, useCallback } from 'react';
import { FolderPlus, Plus, X, Check } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import {
  Crate,
  CRATE_MAX_SLOTS,
  getCrates,
  createCrate,
  addVideoToCrate,
} from '@/services/crates.api';

interface AddToCratePopoverProps {
  videoId: string | null;
  videoTitle?: string;
  open: boolean;
  onClose: () => void;
}

const FULL_CRATE_MESSAGE = 'Crate full — pull something out first';

const AddToCratePopover: React.FC<AddToCratePopoverProps> = ({
  videoId,
  videoTitle,
  open,
  onClose,
}) => {
  const [crates, setCrates] = useState<Crate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const loadCrates = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getCrates();
      setCrates(result);
    } catch {
      console.error('Failed to load crates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setNewName('');
      setIsCreating(false);
      loadCrates();
    }
  }, [open, loadCrates]);

  const handleAdd = async (crate: Crate) => {
    if (!videoId || crate.videoCount >= CRATE_MAX_SLOTS) return;
    setPendingId(crate.id);
    try {
      const updated = await addVideoToCrate(crate.id, videoId);
      setCrates(prev => prev.map(item => (item.id === updated.id ? updated : item)));
      onClose();
    } catch {
      console.error('Failed to add to crate');
    } finally {
      setPendingId(null);
    }
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (trimmed.length === 0 || !videoId) return;
    setIsCreating(true);
    try {
      const created = await createCrate(trimmed);
      const updated = await addVideoToCrate(created.id, videoId);
      setCrates(prev => [updated, ...prev]);
      onClose();
    } catch {
      console.error('Failed to create crate');
    } finally {
      setIsCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Add to crate"
        className="w-full max-w-[min(24rem,calc(100vw-2rem))] rounded-xl bg-white dark:bg-bg-dark-secondary border border-meradio-light dark:border-border-dark shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-meradio-light dark:border-border-dark">
          <div className="flex items-center gap-2 min-w-0">
            <FolderPlus className="w-5 h-5 text-meradio-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-meradio-dark dark:text-text-dark-primary">
                Add to crate
              </p>
              {videoTitle && (
                <p className="text-xs text-meradio-dark-light dark:text-text-dark-tertiary truncate">
                  {videoTitle}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto p-2">
          {isLoading ? (
            <p className="px-3 py-6 text-center text-sm text-meradio-dark-light dark:text-text-dark-secondary">
              Loading crates…
            </p>
          ) : crates.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-meradio-dark-light dark:text-text-dark-secondary">
              No crates yet — create one below.
            </p>
          ) : (
            crates.map((crate) => {
              const isFull = crate.videoCount >= CRATE_MAX_SLOTS;
              return (
                <button
                  key={crate.id}
                  onClick={() => handleAdd(crate)}
                  disabled={isFull || pendingId === crate.id}
                  title={isFull ? FULL_CRATE_MESSAGE : undefined}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors hover:bg-meradio-light/40 dark:hover:bg-bg-dark-tertiary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-meradio-dark dark:text-text-dark-primary truncate">
                      {crate.name}
                    </span>
                    {isFull && (
                      <span className="block text-xs text-danger-600 dark:text-danger-400">
                        {FULL_CRATE_MESSAGE}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs tabular-nums text-meradio-dark-light dark:text-text-dark-tertiary">
                      {crate.videoCount}/{CRATE_MAX_SLOTS}
                    </span>
                    {pendingId === crate.id && (
                      <Check className="w-4 h-4 text-meradio-primary" />
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="border-t border-meradio-light dark:border-border-dark p-3">
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              placeholder="New crate name"
              aria-label="New crate name"
              maxLength={60}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              isLoading={isCreating}
              disabled={newName.trim().length === 0}
              aria-label="Create crate"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToCratePopover;
